import axios, { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import styled from "styled-components";
import Listing from "../../common/models/Listing";
import MasterCopy from "../../common/models/MasterCopy";
import SongCollection from "../../common/models/SongCollections";
import { User, UserDetailResponse } from "../../common/models/User";
import { getRemainingListingTime } from "../../common/utils/Time";
import { useAuth } from "../../hooks/useAuth";
import eth_icon from "../../imgs/eth_icon_blue.svg";
import Song, { SongType } from "../../songdetail/models/Song";
import Column from "../Column/Column";
import RoundedProflePic from "../Images/RoundedProfilePic";
import Row from "../Row/Row";
import Text from "../Text/Text";

interface MasterCopyProps {
  song: MasterCopy;
  onSongClick: (song: MasterCopy) => void;
  showLicensedBanner?: boolean;
  showRevokedBanner?: boolean;
  isOwner?: boolean;
  width?: string;
  listing?: Listing;
}

interface CollectionCardProps {
  collection: SongCollection;
  onCollectionClick: (collection: SongCollection) => void;
  width?: string;
}

interface SongCardProps {
  song: Song;
  onSongClicked: (song: Song) => void;
  width?: string;
}

const SongInfo = styled(Column)`
  border-radius: 0px 0px 30px 30px;
  justify-content: flex-start;
  padding-right: 10px;
`;

const CollectionSongInfo = styled(SongInfo)`
  padding-inline: 5px;
  padding-bottom: 5px;
  padding-top: 0px;
`;

const SongInfoProfilePic = styled(RoundedProflePic)`
  width: 40px;
  height: 40px;
  margin-bottom: 10px;
  margin-right: 5px;
`;

const SongInfoName = styled(Text)`
  margin-left: 5px;
  margin-top: 0px;
  margin-bottom: 0px;
  font-weight: 600;
  font-size: 18px;

  @media screen and (max-width: 1440px) {
    font-size: 14px;
  }
`;

const CollectionSongInfoName = styled(SongInfoName)`
  margin-left: 0px;
  margin-top: 0px;
  margin-bottom: 0px;
  font-weight: 600;
  font-size: 18px;

  display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;

  @media screen and (max-width: 1440px) {
    font-size: 14px;
  }
`;

const CollectionArtistInfo = styled(Row)`
  justify-content: space-between;
  padding-inline: 10px;
`;

const ArtistInfoText = styled(Text)`
  font-size: 16px;
  text-align: center;

  @media screen and (max-width: 1440px) {
    font-size: 12px;
  }
`;
const PriceTextContainer = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;

  color: ${(props) => (props.theme.themeType === "dark" ? "white" : "black")};

  @media screen and (max-width: 1440px) {
    font-size: 12px;
  }
`;

const CollectionPriceContainer = styled(PriceTextContainer)`
  margin-left: 0;
  margin-right: auto;
`;

const SongCardContainer = styled("div")<{ width?: string }>`
  position: relative;
  width: ${(props) => (props.width ? props.width : "23%")};
  background: ${(props) =>
    props.theme.themeType === "dark" ? "rgba(196, 196, 196, 0.1)" : "white"};
  border: ${(props) =>
    props.theme.themeType === "dark"
      ? "0.2px solid #FFFFFF40"
      : "1px solid #702EFF"};
  border-radius: 10px;
  margin-top: 1%;
  margin-bottom: 1%;

  &:hover {
    cursor: pointer;
  }

  @media screen and (max-width: 650px) {
    width: 100%;
    margin-top: 4%;
    margin-bottom: 4%;

  }
`;

const SongCoverImg = styled("img")`
  width: 100%;
  min-height: 15vh;
  max-height: 15vh;
  object-fit: cover;
  border-radius: 10px 8px 0 0;
`;

const CollectionSongCoverImg = styled(SongCoverImg)`
  min-height: 15vh;
  max-height: 15vh;
`;

const CollectionArtistColumn = styled(Column)`
align-items: flex-start;

`;

const LaunchBanner = styled.div`
  position: absolute;
  bottom: 38%;
  text-align: center;
  background: rgba(21, 40, 90, 0.75);
  padding-block: 3%;
  width: 100%;

  @media screen and (max-width: 650px) {
    bottom: 28%;
  }

  @media screen and (min-width: 651px) and (max-width: 1440px) {
    bottom: 42%;
    font-size: 12px;
  }

  @media screen and (min-width: 1441px) and (max-width: 1600px) {
    bottom: 50%;
    font-size: 12px;
  }
`;

const MasterCopyCard = ({
  song,
  onSongClick,
  showLicensedBanner,
  showRevokedBanner,
  isOwner,
  width,
  listing,
}: MasterCopyProps) => {
  const onSongClickGrid = () => {
    onSongClick(song);
  };
  const [artist, setArtist] = useState<User>();
  const [listingTime, setListingTime] = useState<{
    days: number;
    hrs: number;
    mnts: number;
    seconds: number;
  }>();

  const auth = useAuth();
  useEffect(() => {
    axios
      .get<UserDetailResponse>(
        process.env.REACT_APP_BACKEND_URL +
          "/get_user_details?wallet_address=" +
          song.artist_wallet_address
      )
      .then((response: AxiosResponse<UserDetailResponse>) => {
        const user = response.data.userDetails as User;
        setArtist(user);
      });
  }, [song.artist_wallet_address]);

  useEffect(() => {
    listing && setListingTime(getRemainingListingTime(listing.start_time));
  }, [listing]);

  return (
    <>
      {!song.for_sale ? (
        isOwner && (
          <SongCardContainer onClick={onSongClickGrid} width={width}>
            <SongCoverImg src={song.song_cover_s3_link} />
            {listing &&
            parseInt(listing.start_time) > new Date().getTime() / 1000 &&
            listingTime && (
              <LaunchBanner>
                Launch in {listingTime["days"]} days {listingTime["hrs"]} hours{" "}
                {listingTime["mnts"]} minutes
              </LaunchBanner>
            )}
            <CollectionSongInfo>
              {artist && (
                <CollectionArtistInfo>
                  <CollectionArtistColumn>
                    <ArtistInfoText style={{ marginBlock: "0" }}>
                      {artist.stage_name}
                    </ArtistInfoText>
                    <CollectionSongInfoName>
                      {song.song_name}
                    </CollectionSongInfoName>
                  </CollectionArtistColumn>
                  <SongInfoProfilePic bg={artist.profile_pic_s3_link} />
                </CollectionArtistInfo>
              )}

              <hr
                style={{ borderTop: "0.5px solid #ffffff25", width: "100%" }}
              />

              <Row align={"flex-end"}>
                <Column style={{ textAlign: "center" }}>
                  <ArtistInfoText style={{ marginBlock: "0", fontSize: '12px' }}>
                    Master Copy
                  </ArtistInfoText>
                  {song.artist_wallet_address === auth.user.wallet_address ? 'Created' : 'Owned'}
                </Column>
              </Row>
            </CollectionSongInfo>
          </SongCardContainer>
        )
      ) : (
        <SongCardContainer onClick={onSongClickGrid} width={width}>
          <SongCoverImg src={song.song_cover_s3_link} />
          {listing &&
            parseInt(listing.start_time) > new Date().getTime() / 1000 &&
            listingTime && (
              <LaunchBanner>
                Launch in {listingTime["days"]} days {listingTime["hrs"]} hours{" "}
                {listingTime["mnts"]} minutes
              </LaunchBanner>
            )}

          <CollectionSongInfo>
            {artist && (
              <CollectionArtistInfo>
                <CollectionArtistColumn>
                  <ArtistInfoText style={{ marginBlock: "0" }}>
                    {artist.stage_name}
                  </ArtistInfoText>
                  <CollectionSongInfoName>
                    {song.song_name}
                  </CollectionSongInfoName>
                </CollectionArtistColumn>
                <SongInfoProfilePic bg={artist.profile_pic_s3_link} />
              </CollectionArtistInfo>
            )}

            <hr style={{ borderTop: "0.5px solid #ffffff25", width: "100%" }} />

            <Row align={"space-between"}>
              <Column>
                <ArtistInfoText style={{ marginBlock: "0",  fontSize: '12px'}}>
                  Price
                </ArtistInfoText>
                <CollectionPriceContainer>
                  <>
                  <img src={eth_icon} width={'18px'} height={'18px'}/>
                    {song.price} ETH
                  </>
                </CollectionPriceContainer>
              </Column>

              <Column style={{ textAlign: "center" }}>
                <ArtistInfoText style={{ marginBlock: "0",  fontSize: '12px' }}>
                  License
                </ArtistInfoText>
                {song.license_address !== null
                  ? showRevokedBanner
                    ? "Revoked"
                    : showLicensedBanner
                    ? "Owned"
                    : "Available"
                  : "Unavailable"}
              </Column>
            </Row>
          </CollectionSongInfo>
        </SongCardContainer>
      )}
    </>
  );
};

export const CollectionCard = ({
  collection,
  onCollectionClick,
  width,
}: CollectionCardProps) => {
  const onSongClick = () => {
    onCollectionClick(collection);
  };
  const [artist, setArtist] = useState<User>();
  useEffect(() => {
    axios
      .get<UserDetailResponse>(
        process.env.REACT_APP_BACKEND_URL +
          "/get_user_details?wallet_address=" +
          collection.artist_wallet_address
      )
      .then((response: AxiosResponse<UserDetailResponse>) => {
        const user = response.data.userDetails as User;
        setArtist(user);
      });
  }, [collection.artist_wallet_address]);

  return (
    <>
      <SongCardContainer onClick={onSongClick} width={width}>
        <CollectionSongCoverImg src={collection.song_cover_s3_link} />
        {collection.launch_date > new Date().getTime() / 1000 && (
          <LaunchBanner>
            Launch in{" "}
            {getRemainingListingTime(collection.launch_date.toString())["days"]}{" "}
            days{" "}
            {getRemainingListingTime(collection.launch_date.toString())["hrs"]}{" "}
            hours{" "}
            {getRemainingListingTime(collection.launch_date.toString())["mnts"]}{" "}
            minutes
          </LaunchBanner>
        )}

        <CollectionSongInfo>
          {artist && (
            <CollectionArtistInfo>
              <CollectionArtistColumn>
                <ArtistInfoText style={{ marginBlock: "0" }}>
                  {artist.stage_name}
                </ArtistInfoText>
                <CollectionSongInfoName>
                  {collection.song_name}
                </CollectionSongInfoName>
              </CollectionArtistColumn>
              <SongInfoProfilePic bg={artist.profile_pic_s3_link} />
            </CollectionArtistInfo>
          )}

          <hr style={{ borderTop: "0.5px solid #ffffff25", width: "100%" }} />
          <Row align={"space-between"} style={{paddingInline: '10px'}}>
            {collection.for_secondary_sale ? (
              <>
                <Column>
                  <ArtistInfoText style={{ marginBlock: "0", fontSize: '12px' }}>
                    Floor Price
                  </ArtistInfoText>
                  <CollectionPriceContainer>
                    <>
                  <img src={eth_icon} width={'18px'} height={'18px'}/>
                      {collection.secondary_sale_qty > 0
                        ? collection.floor_price
                        : "-"}{" "}
                      ETH
                    </>
                  </CollectionPriceContainer>
                </Column>

                <Column
                  style={{ justifyContent: "center", alignItems: "center" }}
                >
                  <ArtistInfoText style={{ marginBlock: "0" , fontSize: '12px'}}>
                    # For Resale
                  </ArtistInfoText>
                  <ArtistInfoText style={{ marginBlock: "0" }}>
                    {collection.secondary_sale_qty}
                  </ArtistInfoText>
                </Column>
              </>
            ) : (
              <>
                <Column>
                  <ArtistInfoText style={{ marginBlock: "0",  fontSize: '12px' }}>
                    Mint Price
                  </ArtistInfoText>
                  <CollectionPriceContainer>
                    {collection.mint_price > 0 && (
                      <>
                      <img src={eth_icon} width={'18px'} height={'18px'}/>
                        {collection.mint_price} ETH
                      </>
                    )}
                  </CollectionPriceContainer>
                </Column>

                <Column
                  style={{ justifyContent: "center", alignItems: "center" }}
                >
                  <ArtistInfoText style={{ marginBlock: "0",  fontSize: '12px' }}>
                    Available to Mint
                  </ArtistInfoText>
                  <ArtistInfoText style={{ marginBlock: "0" }}>
                    {collection.quantity - collection.minted > 0
                      ? `${collection.quantity - collection.minted}/${
                          collection.quantity
                        }`
                      : "Sold Out"}
                  </ArtistInfoText>
                </Column>
              </>
            )}
          </Row>
        </CollectionSongInfo>
      </SongCardContainer>
    </>
  );
};

export const SongCard = ({ song, onSongClicked, width }: SongCardProps) => {
  const onSongClick = () => {
    onSongClicked(song);
  };
  const auth = useAuth();
  const [artist, setArtist] = useState<User>();
  useEffect(() => {
    axios
      .get<UserDetailResponse>(
        process.env.REACT_APP_BACKEND_URL +
          "/get_user_details?wallet_address=" +
          song.artist_wallet_address
      )
      .then((response: AxiosResponse<UserDetailResponse>) => {
        const user = response.data.userDetails as User;
        setArtist(user);
      });
  }, [song.artist_wallet_address]);

  const isCurrentDrop = () => {
    return song.type === SongType.COLLECTIBLE && song.minted <= song.quantity;
  };

  const isOwnedToken = () => {
    return (
      song.type === SongType.TOKEN &&
      song.owner_address === auth.user.wallet_address
    );
  };

  return (
    <>
      <SongCardContainer onClick={onSongClick} width={width}>
        <CollectionSongCoverImg src={song.song_cover_s3_link} />
        <CollectionSongInfo>
          {artist && (
            <CollectionArtistInfo>
              <Column>
                <ArtistInfoText style={{ marginBlock: "0" }}>
                  {artist.stage_name}
                </ArtistInfoText>
                <CollectionSongInfoName>
                  {song.song_name}
                </CollectionSongInfoName>
              </Column>
              <SongInfoProfilePic bg={artist.profile_pic_s3_link} />
            </CollectionArtistInfo>
          )}

          <hr style={{ borderTop: "0.5px solid #ffffff25", width: "100%" }} />
          {isCurrentDrop() && (
            <Row align={"space-between"} style={{paddingInline: '10px'}}>
              <Column>
                <ArtistInfoText style={{ marginBlock: "0",  fontSize: '12px' }}>
                  Mint Price
                </ArtistInfoText>
                <CollectionPriceContainer>
                  {song.price > 0 && (
                    <>
                      <img src={eth_icon} width={'18px'} height={'18px'}/>
                      {song.price} ETH
                    </>
                  )}
                </CollectionPriceContainer>
              </Column>

              <Column
                style={{ justifyContent: "center", alignItems: "center" }}
              >
                <ArtistInfoText style={{ marginBlock: "0",  fontSize: '12px' }}>
                  Available to Mint
                </ArtistInfoText>
                <ArtistInfoText style={{ marginBlock: "0" }}>
                  {song.quantity - song.minted > 0
                    ? `${song.quantity - song.minted}/${song.quantity}`
                    : "Sold Out"}
                </ArtistInfoText>
              </Column>
            </Row>
          )}

          {isOwnedToken() && (
            <Row align={"space-between"} style={{paddingInline: '10px'}}>
              <Column>
                <ArtistInfoText style={{ marginBlock: "0", fontSize: '12px' }}>
                  Collectible
                </ArtistInfoText>
                <ArtistInfoText
                  style={{ marginBlock: "0", textAlign: "center" }}
                >
                  #{song.tokenId}
                </ArtistInfoText>
              </Column>

              <Column
                style={{ justifyContent: "center", alignItems: "center" }}
              >
                <ArtistInfoText style={{ marginBlock: "0", fontSize: '12px' }}>
                  Status
                </ArtistInfoText>
                <ArtistInfoText style={{ marginBlock: "0" }}>
                  Owned
                </ArtistInfoText>
              </Column>
            </Row>
          )}
        </CollectionSongInfo>
      </SongCardContainer>
    </>
  );
};

export default MasterCopyCard;
